import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import {
  getKBCategories, createKBCategory, deleteKBCategory,
  getKBArticles, getKBArticle, createKBArticle, updateKBArticle, deleteKBArticle,
  uploadKBImage, getClients,
} from "../api";
import {
  BookOpen, Plus, Search, Trash2, Edit2, X, Tag, Folder, ChevronRight,
  Eye, EyeOff, Globe, Lock, Bold, Italic, Underline, List, ListOrdered,
  Image, Link, Code, Heading1, Heading2, AlignLeft, Save, ArrowLeft,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

// =============================================================
//  HELPERS
// =============================================================
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";

const S = {
  page:     { padding:24, maxWidth:1400, margin:"0 auto" },
  card:     { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:10 },
  input:    { background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:13, width:"100%", boxSizing:"border-box" },
  btn:      { background:"linear-gradient(135deg,#4f6fff,#7b61ff)", color:"#fff", padding:"9px 16px", borderRadius:10, fontSize:13, fontWeight:700, border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, boxShadow:"0 0 18px rgba(79,111,255,0.3)" },
  btnGhost: { background:"transparent", color:"var(--t-text-label)", padding:"6px 10px", borderRadius:6, fontSize:12, border: "1px solid var(--t-border-soft)", cursor:"pointer", display:"flex", alignItems:"center", gap:4 },
  btnDanger:{ background:"#7f1d1d", color:"#fca5a5", padding:"6px 10px", borderRadius:6, fontSize:12, border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:4 },
  label:    { fontSize:12, color:"var(--t-text-muted)", marginBottom:4, display:"block" },
};

// =============================================================
//  RICH TEXT EDITOR
// =============================================================
function RichEditor({ value, onChange, onImageUpload }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, []); // only on mount

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || "");
  };

  const handleImageInsert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await onImageUpload(file);
      const url = `${API_URL}${res.data.url}`;
      exec("insertHTML", `<img src="${url}" alt="" style="max-width:100%;border-radius:6px;margin:8px 0" />`);
    } catch { alert("Image upload failed"); }
    e.target.value = "";
  };

  const handleLink = () => {
    const url = prompt("Enter URL:");
    if (url) exec("createLink", url);
  };

  const imgInputRef = useRef(null);

  const toolBtn = (icon, cmd, val, title) => (
    <button
      key={title}
      onMouseDown={(e) => { e.preventDefault(); exec(cmd, val); }}
      title={title}
      style={{ background:"transparent", border:"none", color:"var(--t-text-muted)", cursor:"pointer", padding:"4px 6px", borderRadius:4, display:"flex", alignItems:"center" }}
      onMouseEnter={e => e.currentTarget.style.background="#1e293b"}
      onMouseLeave={e => e.currentTarget.style.background="transparent"}
    >
      {icon}
    </button>
  );

  return (
    <div style={{ border:"1px solid var(--t-border-strong)", borderRadius:8, overflow:"hidden" }}>
      {/* Toolbar */}
      <div style={{ background:"var(--t-surface-mid)", borderBottom:"1px solid #334155", padding:"4px 8px", display:"flex", gap:2, flexWrap:"wrap", alignItems:"center" }}>
        {toolBtn(<Bold size={14}/>,          "bold",              null,          "Bold")}
        {toolBtn(<Italic size={14}/>,        "italic",            null,          "Italic")}
        {toolBtn(<Underline size={14}/>,     "underline",         null,          "Underline")}
        <div style={{ width:1, height:18, background:"var(--t-border-strong)", margin:"0 4px" }} />
        {toolBtn(<Heading1 size={14}/>,      "formatBlock",       "H1",          "Heading 1")}
        {toolBtn(<Heading2 size={14}/>,      "formatBlock",       "H2",          "Heading 2")}
        {toolBtn(<AlignLeft size={14}/>,     "formatBlock",       "P",           "Paragraph")}
        <div style={{ width:1, height:18, background:"var(--t-border-strong)", margin:"0 4px" }} />
        {toolBtn(<List size={14}/>,          "insertUnorderedList",null,          "Bullet List")}
        {toolBtn(<ListOrdered size={14}/>,   "insertOrderedList",  null,          "Numbered List")}
        <div style={{ width:1, height:18, background:"var(--t-border-strong)", margin:"0 4px" }} />
        {toolBtn(<Code size={14}/>,          "formatBlock",       "PRE",         "Code Block")}
        <button
          onMouseDown={(e) => { e.preventDefault(); handleLink(); }}
          title="Insert Link"
          style={{ background:"transparent", border:"none", color:"var(--t-text-muted)", cursor:"pointer", padding:"4px 6px", borderRadius:4, display:"flex", alignItems:"center" }}
          onMouseEnter={e => e.currentTarget.style.background="#1e293b"}
          onMouseLeave={e => e.currentTarget.style.background="transparent"}
        >
          <Link size={14} />
        </button>
        <button
          onMouseDown={(e) => { e.preventDefault(); imgInputRef.current?.click(); }}
          title="Insert Image"
          style={{ background:"transparent", border:"none", color:"var(--t-text-muted)", cursor:"pointer", padding:"4px 6px", borderRadius:4, display:"flex", alignItems:"center" }}
          onMouseEnter={e => e.currentTarget.style.background="#1e293b"}
          onMouseLeave={e => e.currentTarget.style.background="transparent"}
        >
          <Image size={14} />
        </button>
        <input ref={imgInputRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleImageInsert} />
      </div>
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef.current?.innerHTML || "")}
        style={{
          minHeight:300,
          padding:16,
          color:"var(--t-text-body)",
          fontSize:14,
          lineHeight:1.7,
          outline:"none",
          background:"var(--t-page)",
        }}
      />
    </div>
  );
}

// =============================================================
//  ARTICLE EDITOR MODAL
// =============================================================
function ArticleEditor({ article, categories, clients, agentRole, agentClientId, onSave, onClose }) {
  const isMobile  = useIsMobile();
  const isAdmin   = agentRole === "admin";
  const isManager = agentRole === "manager";

  const [form, setForm] = useState({
    title:       article?.title || "",
    content:     article?.content || "",
    excerpt:     article?.excerpt || "",
    category_id: article?.category_id || "",
    client_id:   article?.client_id || (isManager ? agentClientId : ""),
    is_published:article?.is_published !== false,
    tags:        article?.tags || [],
  });
  const [tagInput, setTagInput]   = useState("");
  const [saving,   setSaving]     = useState(false);
  const [error,    setError]      = useState("");

  const addTag = (e) => {
    if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().toLowerCase();
      if (!form.tags.includes(t)) setForm(p => ({ ...p, tags: [...p.tags, t] }));
      setTagInput("");
    }
  };
  const removeTag = (t) => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }));

  const handleSubmit = async () => {
    if (!form.title.trim()) return setError("Title is required");
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        category_id: form.category_id || null,
        client_id:   isManager ? agentClientId : (form.client_id || null),
      };
      if (article?.id) await updateKBArticle(article.id, payload);
      else             await createKBArticle(payload);
      onSave();
    } catch (err) { setError(err.response?.data?.error || "Failed to save"); }
    finally { setSaving(false); }
  };

  const relevantCategories = categories.filter(c =>
    isAdmin ? true : (c.client_id === null || c.client_id === agentClientId)
  );

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"flex-start", justifyContent:"center", overflowY:"auto", padding: isMobile ? 16 : "40px 20px" }}>
      <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:12, width:"100%", maxWidth:860, padding: isMobile ? 16 : 28 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h2 style={{ color:"var(--t-text-primary)", fontSize:18, fontWeight:700 }}>{article ? "Edit Article" : "New KB Article"}</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--t-text-label)", cursor:"pointer" }}><X size={18} /></button>
        </div>

        {error && <div style={{ background:"#7f1d1d", color:"#fca5a5", padding:"8px 12px", borderRadius:6, marginBottom:12, fontSize:13 }}>{error}</div>}

        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 280px", gap:20 }}>
          {/* Left column */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={S.label}>Title *</label>
              <input style={S.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Article title" />
            </div>
            <div>
              <label style={S.label}>Content</label>
              <RichEditor
                value={form.content}
                onChange={v => setForm(p => ({ ...p, content: v }))}
                onImageUpload={uploadKBImage}
              />
            </div>
            <div>
              <label style={S.label}>Excerpt (auto-generated if blank)</label>
              <textarea
                style={{ ...S.input, height:70, resize:"vertical" }}
                value={form.excerpt}
                onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))}
                placeholder="Short summary shown in article lists..."
              />
            </div>
          </div>

          {/* Right column */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={S.label}>Category</label>
              <select style={S.input} value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))}>
                <option value="">— None —</option>
                {relevantCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.company_name ? ` (${c.company_name})` : ""}</option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div>
                <label style={S.label}>Visibility</label>
                <select
                  style={S.input}
                  value={form.client_id || ""}
                  onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}
                >
                  <option value="">Global (public link)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name} (private)</option>)}
                </select>
              </div>
            )}
            {isManager && (
              <div style={{ background:"var(--t-surface-mid)", borderRadius:8, padding:"8px 12px", fontSize:12, color:"var(--t-text-label)", display:"flex", alignItems:"center", gap:6 }}>
                <Lock size={12} /> Article will be private to your client
              </div>
            )}

            <div>
              <label style={S.label}>Tags</label>
              <input
                style={S.input}
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={addTag}
                placeholder="Type tag + Enter"
              />
              <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                {form.tags.map(t => (
                  <span key={t} style={{ background:"rgba(99,162,255,0.15)", color:"var(--badge-blue)", fontSize:11, padding:"2px 8px", borderRadius:20, display:"flex", alignItems:"center", gap:4 }}>
                    {t}
                    <button onClick={() => removeTag(t)} style={{ background:"none", border:"none", color:"var(--badge-blue)", cursor:"pointer", padding:0, lineHeight:1 }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:10, background:"var(--t-surface-mid)", borderRadius:8, padding:"10px 12px" }}>
              <input
                type="checkbox"
                id="kb-published"
                checked={form.is_published}
                onChange={e => setForm(p => ({ ...p, is_published: e.target.checked }))}
                style={{ width:14, height:14, cursor:"pointer", accentColor:"#4f6fff" }}
              />
              <label htmlFor="kb-published" style={{ fontSize:13, color:"var(--t-text-body)", cursor:"pointer" }}>Published (visible to users)</label>
            </div>

            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{ ...S.btn, justifyContent:"center", marginTop:"auto", opacity: saving ? 0.6 : 1 }}
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Article"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  CATEGORY MANAGER MODAL
// =============================================================
function CategoryManager({ categories, clients, agentRole, agentClientId, onClose, onRefresh }) {
  const isMobile  = useIsMobile();
  const isAdmin   = agentRole === "admin";
  const [form, setForm] = useState({ name:"", client_id: isAdmin ? "" : agentClientId, color:"#4f6fff" });
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await createKBCategory({ ...form, client_id: isAdmin ? (form.client_id || null) : agentClientId });
      setForm({ name:"", client_id: isAdmin ? "" : agentClientId, color:"#4f6fff" });
      onRefresh();
    } catch (err) { alert(err.response?.data?.error || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category? Articles will become uncategorized.")) return;
    try { await deleteKBCategory(id); onRefresh(); }
    catch (err) { alert(err.response?.data?.error || "Failed"); }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:12, width:"100%", maxWidth:480, padding: isMobile ? 16 : 24, maxHeight:"80vh", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h2 style={{ color:"var(--t-text-primary)", fontSize:16, fontWeight:700 }}>Manage Categories</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--t-text-label)", cursor:"pointer" }}><X size={16} /></button>
        </div>

        <form onSubmit={handleCreate} style={{ display:"flex", flexWrap: isMobile ? "wrap" : "nowrap", gap:8, marginBottom:16 }}>
          <input
            style={{ ...S.input, flex:1, minWidth: isMobile ? 140 : undefined }}
            placeholder="Category name"
            value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          />
          <input
            type="color"
            value={form.color}
            onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
            style={{ width:38, height:38, borderRadius:6, border:"1px solid var(--t-border-strong)", background:"var(--t-surface-mid)", cursor:"pointer", padding:2 }}
          />
          {isAdmin && (
            <select style={{ ...S.input, width:140 }} value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))}>
              <option value="">Global</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          )}
          <button type="submit" disabled={saving} style={{ ...S.btn, flexShrink:0 }}>
            <Plus size={14} />
          </button>
        </form>

        <div style={{ overflowY:"auto", display:"flex", flexDirection:"column", gap:6 }}>
          {categories.map(c => (
            <div key={c.id} style={{ background:"var(--t-surface-mid)", borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:c.color, flexShrink:0 }} />
              <span style={{ flex:1, fontSize:13, color:"var(--t-text-body)" }}>{c.name}</span>
              {c.company_name
                ? <span style={{ fontSize:11, color:"var(--t-text-label)" }}>{c.company_name}</span>
                : <span style={{ fontSize:11, color:"#3b82f6", display:"flex", alignItems:"center", gap:3 }}><Globe size={10}/> Global</span>
              }
              <button onClick={() => handleDelete(c.id)} style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", padding:2 }}><Trash2 size={13} /></button>
            </div>
          ))}
          {!categories.length && <p style={{ color:"var(--t-text-deep)", fontSize:13, textAlign:"center" }}>No categories yet</p>}
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  ARTICLE VIEWER
// =============================================================
function ArticleViewer({ article, onEdit, onBack, canEdit }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ ...S.card, padding: isMobile ? 16 : 28, maxWidth:860 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"var(--t-text-label)", cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:13 }}>
          <ArrowLeft size={14} /> Back
        </button>
        {canEdit && (
          <button onClick={onEdit} style={{ ...S.btnGhost, marginLeft:"auto" }}>
            <Edit2 size={12} /> Edit
          </button>
        )}
      </div>

      <div style={{ marginBottom:12 }}>
        {article.category_name && (
          <span style={{ background:article.category_color || "#1e3a5f", color:"var(--badge-blue)", fontSize:11, padding:"2px 8px", borderRadius:20, marginRight:8 }}>
            {article.category_name}
          </span>
        )}
        {article.client_id
          ? <span style={{ fontSize:11, color:"var(--t-text-label)", display:"inline-flex", alignItems:"center", gap:3 }}><Lock size={10}/> {article.company_name || "Private"}</span>
          : <span style={{ fontSize:11, color:"#3b82f6", display:"inline-flex", alignItems:"center", gap:3 }}><Globe size={10}/> Global</span>
        }
      </div>

      <h1 style={{ fontSize:22, fontWeight:700, color:"var(--t-text-primary)", marginBottom:8 }}>{article.title}</h1>
      <p style={{ fontSize:12, color:"var(--t-text-deep)", marginBottom:20 }}>
        By {article.created_by_name || "Unknown"} · Updated {fmtDate(article.updated_at)} · {article.view_count} view{article.view_count !== 1 ? "s" : ""}
      </p>

      <div
        className="kb-content"
        style={{ color:"var(--t-text-body)", lineHeight:1.8, fontSize:14 }}
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {article.tags?.length > 0 && (
        <div style={{ marginTop:24, display:"flex", gap:6, flexWrap:"wrap" }}>
          {article.tags.map(t => (
            <span key={t} style={{ background:"var(--t-surface-mid)", color:"var(--t-text-label)", fontSize:11, padding:"2px 10px", borderRadius:20 }}>
              #{t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================
//  MAIN PAGE
// =============================================================
export default function KnowledgeBase() {
  const { agent }    = useAuth();
  const { id: urlId } = useParams();
  const navigate      = useNavigate();
  const isMobile      = useIsMobile();

  const isAdmin   = agent?.role === "admin" || agent?.roles?.includes("admin");
  const isManager = !isAdmin && (agent?.role === "manager" || agent?.roles?.includes("manager"));
  const canEdit   = isAdmin || isManager;

  const [articles,    setArticles]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filterCat,   setFilterCat]   = useState("");
  const [filterTag,   setFilterTag]   = useState("");
  const [filterClient,setFilterClient]= useState("");
  const [viewArticle, setViewArticle] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [showNewEdit, setShowNewEdit] = useState(false);
  const [showCatMgr,  setShowCatMgr]  = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [artRes, catRes] = await Promise.all([
        getKBArticles({ search: search || undefined, category_id: filterCat || undefined, tag: filterTag || undefined, client_id: filterClient || undefined }),
        getKBCategories(),
      ]);
      setArticles(artRes.data);
      setCategories(catRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterCat, filterTag, filterClient]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (isAdmin) getClients().then(r => setClients(r.data)).catch(() => {});
  }, [isAdmin]);

  // Load article from URL param
  useEffect(() => {
    if (urlId && !viewArticle) {
      getKBArticle(urlId).then(r => setViewArticle(r.data)).catch(() => {});
    }
  }, [urlId]); // eslint-disable-line

  const handleView = async (id) => {
    try {
      const res = await getKBArticle(id);
      setViewArticle(res.data);
      navigate(`/knowledge-base/${id}`, { replace: true });
    } catch {}
  };

  const handleBack = () => {
    setViewArticle(null);
    navigate("/knowledge-base", { replace: true });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await deleteKBArticle(id); loadData(); if (viewArticle?.id === id) handleBack(); }
    catch (err) { alert(err.response?.data?.error || "Failed"); }
  };

  const handleSaved = () => {
    setShowNewEdit(false);
    setEditArticle(null);
    loadData();
  };

  const allTags = [...new Set(articles.flatMap(a => a.tags || []))].sort();

  if (viewArticle && !showNewEdit) {
    return (
      <div style={{ padding: isMobile ? 14 : 24 }} className="fade-in">
        <ArticleViewer
          article={viewArticle}
          canEdit={canEdit}
          onBack={handleBack}
          onEdit={() => { setEditArticle(viewArticle); setShowNewEdit(true); }}
        />
        {showNewEdit && (
          <ArticleEditor
            article={editArticle}
            categories={categories}
            clients={clients}
            agentRole={agent?.role}
            agentClientId={agent?.client_id}
            onSave={async () => { handleSaved(); const res = await getKBArticle(viewArticle.id); setViewArticle(res.data); }}
            onClose={() => { setShowNewEdit(false); setEditArticle(null); }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ ...S.page, padding: isMobile ? 14 : 24 }} className="fade-in">
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <BookOpen size={22} style={{ color:"#3b82f6" }} />
          <div>
            <h1 style={{ fontSize:22, fontWeight:700, color:"var(--t-text-primary)" }}>Knowledge Base</h1>
            <p style={{ fontSize:13, color:"var(--t-text-label)" }}>{articles.length} article{articles.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {canEdit && (
            <button onClick={() => setShowCatMgr(true)} style={S.btnGhost}>
              <Folder size={13} /> Categories
            </button>
          )}
          {canEdit && (
            <button onClick={() => { setEditArticle(null); setShowNewEdit(true); }} style={S.btn}>
              <Plus size={15} /> New Article
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ position:"relative", flex:1, minWidth:200 }}>
          <Search size={14} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"var(--t-text-deep)" }} />
          <input
            style={{ ...S.input, paddingLeft:32 }}
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select style={{ ...S.input, width:160 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {allTags.length > 0 && (
          <select style={{ ...S.input, width:140 }} value={filterTag} onChange={e => setFilterTag(e.target.value)}>
            <option value="">All Tags</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {isAdmin && clients.length > 0 && (
          <select style={{ ...S.input, width:160 }} value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="">All Clients</option>
            <option value="null">Global Only</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        )}
      </div>

      {/* Article grid */}
      {loading ? (
        <div style={{ display:"flex", justifyContent:"center", padding:48 }}>
          <div style={{ width:28, height:28, border:"3px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%" }} className="animate-spin" />
        </div>
      ) : articles.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, color:"var(--t-text-deep)" }}>
          <BookOpen size={40} style={{ marginBottom:12, opacity:0.3 }} />
          <p style={{ fontSize:14 }}>No articles found</p>
          {canEdit && <p style={{ fontSize:12, marginTop:4 }}>Create your first article to get started</p>}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:14 }}>
          {articles.map(a => (
            <div
              key={a.id}
              style={{ ...S.card, padding:18, cursor:"pointer", transition:"border-color .15s" }}
              onClick={() => handleView(a.id)}
              onMouseEnter={e => e.currentTarget.style.borderColor="var(--t-border-strong)"}
              onMouseLeave={e => e.currentTarget.style.borderColor="#1e293b"}
            >
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {a.category_name && (
                    <span style={{ background: a.category_color || "#1e3a5f", color:"var(--badge-blue)", fontSize:10, padding:"2px 7px", borderRadius:20 }}>
                      {a.category_name}
                    </span>
                  )}
                  {a.client_id
                    ? <span style={{ fontSize:10, color:"var(--t-text-label)", display:"inline-flex", alignItems:"center", gap:2 }}><Lock size={9}/> Private</span>
                    : <span style={{ fontSize:10, color:"#3b82f6", display:"inline-flex", alignItems:"center", gap:2 }}><Globe size={9}/> Public</span>
                  }
                  {!a.is_published && (
                    <span style={{ background:"var(--t-surface-mid)", color:"var(--t-text-label)", fontSize:10, padding:"2px 7px", borderRadius:20, display:"inline-flex", alignItems:"center", gap:2 }}>
                      <EyeOff size={9}/> Draft
                    </span>
                  )}
                </div>
                {canEdit && (
                  <div style={{ display:"flex", gap:4 }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => { setEditArticle(a); setShowNewEdit(true); }}
                      style={{ background:"none", border:"none", color:"var(--t-text-deep)", cursor:"pointer", padding:"2px 4px" }}
                    >
                      <Edit2 size={12} />
                    </button>
                    <button
                      onClick={() => handleDelete(a.id)}
                      style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", padding:"2px 4px" }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>

              <h3 style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)", marginBottom:6, lineHeight:1.4 }}>{a.title}</h3>
              {a.excerpt && (
                <p style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.6, marginBottom:10, display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                  {a.excerpt.replace(/<[^>]+>/g, "")}
                </p>
              )}

              {a.tags?.length > 0 && (
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:10 }}>
                  {a.tags.slice(0, 4).map(t => (
                    <span key={t} style={{ background:"var(--t-surface-mid)", color:"var(--t-text-label)", fontSize:10, padding:"1px 7px", borderRadius:20 }}>#{t}</span>
                  ))}
                  {a.tags.length > 4 && <span style={{ fontSize:10, color:"var(--t-text-deep)" }}>+{a.tags.length - 4}</span>}
                </div>
              )}

              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", borderTop: "1px solid var(--t-border-soft)", paddingTop:8, marginTop:4 }}>
                <span style={{ fontSize:11, color:"var(--t-text-deep)" }}>{fmtDate(a.updated_at)}</span>
                <span style={{ fontSize:11, color:"var(--t-text-deep)", display:"flex", alignItems:"center", gap:3 }}>
                  <Eye size={10}/> {a.view_count}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showNewEdit && (
        <ArticleEditor
          article={editArticle}
          categories={categories}
          clients={clients}
          agentRole={agent?.role}
          agentClientId={agent?.client_id}
          onSave={handleSaved}
          onClose={() => { setShowNewEdit(false); setEditArticle(null); }}
        />
      )}
      {showCatMgr && (
        <CategoryManager
          categories={categories}
          clients={clients}
          agentRole={agent?.role}
          agentClientId={agent?.client_id}
          onClose={() => setShowCatMgr(false)}
          onRefresh={loadData}
        />
      )}

      <style>{`
        .kb-content h1 { font-size:20px; font-weight:700; color:var(--t-text-primary); margin:20px 0 8px; }
        .kb-content h2 { font-size:17px; font-weight:600; color:var(--t-text-body); margin:16px 0 6px; }
        .kb-content p  { margin:8px 0; }
        .kb-content ul, .kb-content ol { padding-left:20px; margin:8px 0; }
        .kb-content li { margin:4px 0; }
        .kb-content pre { background:var(--t-surface-mid); padding:12px; border-radius:6px; overflow-x:auto; font-size:13px; color:var(--t-text-body); }
        .kb-content code { background:var(--t-surface-mid); padding:2px 6px; border-radius:4px; font-size:12px; color:var(--t-text-muted); }
        .kb-content a { color:#3b82f6; }
        .kb-content img { max-width:100%; border-radius:6px; }
        .kb-content blockquote { border-left:3px solid var(--t-border-strong); margin:8px 0; padding-left:12px; color:var(--t-text-muted); }
      `}</style>
    </div>
  );
}
